import { c as _c } from "react/compiler-runtime";
import posthog from 'posthog-js';
import { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import useSWR, { SWRConfig } from 'swr';
import { CliSession } from '../../../../../../typings/CliSession.interface';
import { DestinationType, DestinationTypeSchema, SourceType, SourceTypeSchema } from '../../../../../../typings/Integration.interface';
import { APIOrganization, APIOrganizationWithRoleAndPlan } from '../../../../../../typings/Organization.interface';
import { OrganizationRole } from '../../../../../../typings/OrganizationMember.interface';
import { APISubscription } from '../../../../../../typings/Subscription.interface';
import { APITeam, APITeamWithRole } from '../../../../../../typings/Team.interface';
import { TeamRole } from '../../../../../../typings/TeamMember.interface';
import { User } from '../../../../../../typings/User.interface';
import APIMethodKeys from '../../../client/APIMethodKeys';
import { dashboard_session_initial_values, DashboardSession } from '../../../typings/DashboardSession.interface';
import { storage_keys } from '../../../utils/storage';
import Icon from '../../common/base/Icon';
import { useToasts } from '../../common/Toast';
import { GlobalContext } from '../../contexts/GlobalContext';
import { UserContext } from '../../contexts/UserContext';
import useEventListener from '../../hooks/useEventListener';
import { useIsDegradedBanner } from '../../hooks/useIsDegradedBanner';
import useLocalStorage from '../../hooks/useLocalStorage';
import useSearchQuery from '../../hooks/useSearchQuery';
import FullscreenLayout from '../../layouts/FullscreenLayout';
export const DashboardContext = createContext<{
  active_cli_sessions: CliSession[];
  user?: User | null;
  team?: APITeam | null;
  organizations?: APIOrganizationWithRoleAndPlan[] | null;
  teams?: APITeamWithRole[] | null;
  mutateTeams: () => Promise<APITeamWithRole[] | undefined>;
  mutateOrganizations: () => Promise<APIOrganizationWithRoleAndPlan[] | undefined>;
  organization?: APIOrganization | null;
  source_types?: { [key in SourceType]: SourceTypeSchema };
  destination_types?: { [key in DestinationType]: DestinationTypeSchema };
  team_role?: TeamRole;
  organization_role?: OrganizationRole;
  subscription?: APISubscription;
  resetContext: () => void;
  mutateTeam: (team: APITeam) => void;
  mutateSubscription: (subscription: APISubscription) => void;
  mutateOrganization: (organization: APIOrganization) => void;
  session: DashboardSession;
  setSession: (session: DashboardSession) => void;
  has_sources: boolean;
  has_connection: boolean;
  has_created_connections: boolean;
  mutateHasConnection: () => void;
  get_started_pending_steps: number;
  has_team_members_or_invites: boolean;
  show_get_started: boolean;
  setShowGetStarted: (show: boolean) => void;
  prev_location: {
    pathname: string;
    search: string;
  };
  nav_collapsed: boolean;
  setNavCollapsed: (collapsed: boolean) => void;
  is_degraded_banner_active: boolean;
  dismissDegradedBanner: () => void;
  logout: () => void;
}>({
  active_cli_sessions: [],
  mutateTeams: async () => undefined,
  mutateOrganizations: async () => undefined,
  mutateTeam: () => {
    return;
  },
  mutateSubscription: () => {
    return;
  },
  mutateOrganization: () => {
    return;
  },
  resetContext: () => {
    return;
  },
  logout: () => {
    return;
  },
  session: {} as DashboardSession,
  setSession: () => {
    return;
  },
  has_sources: false,
  has_connection: false,
  has_created_connections: false,
  mutateHasConnection: () => {
    return;
  },
  get_started_pending_steps: 2,
  has_team_members_or_invites: false,
  show_get_started: true,
  setShowGetStarted: () => {
    return;
  },
  prev_location: {
    pathname: '',
    search: ''
  },
  nav_collapsed: false,
  setNavCollapsed: () => {
    return;
  },
  is_degraded_banner_active: false,
  dismissDegradedBanner: () => {
    return;
  }
});
const getAddTeamKeyMiddleWare = (team_id?: string) => useSWRNext => {
  return (key, fetcher, config) => {
    return typeof key === 'function' ? useSWRNext((...args) => [key(...args), team_id], fetcher, config) : useSWRNext(key && [key, team_id], fetcher, config);
  };
};
let prev_location_cache = null;
export const DashboardContextProvider: React.FC<PropsWithChildren> = t0 => {
  const $ = _c(187);
  const {
    children
  } = t0;
  const {
    HookdeckAPI,
    setAPITeamId
  } = useContext(GlobalContext);
  const {
    loading_user,
    user
  } = useContext(UserContext);
  const history = useHistory();
  const location = useLocation();
  const toasts = useToasts();
  const search = useSearchQuery();
  const [prev_location, setPrevLocation] = useState(location);
  let t1;
  if ($[0] !== HookdeckAPI.team_id) {
    t1 = HookdeckAPI.team_id && [APIMethodKeys.session.context(), HookdeckAPI.team_id];
    $[0] = HookdeckAPI.team_id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== HookdeckAPI.session) {
    t2 = () => HookdeckAPI.session.context();
    $[2] = HookdeckAPI.session;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    data: session_context,
    mutate: mutateContext,
    error: context_error
  } = useSWR(t1, t2, {
    shouldRetryOnError: false,
    onSuccess: context => {
      setLastTeamId(context.team.id);
      setSession({
        ...session,
        team_id: context.team.id
      });
    }
  });
  let t3;
  if ($[4] !== session_context) {
    t3 = session_context || {};
    $[4] = session_context;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    team,
    organization,
    subscription,
    team_role,
    organization_role
  } = t3;
  const is_eg_type = !!team && team.type !== "outpost";
  const [t4, t5] = useLocalStorage(`${storage_keys.session.dashboard_session}:${user?.id}`, dashboard_session_initial_values, true);
  const session = t4;
  const setSession = t5;
  const [last_team_id, t6] = useLocalStorage(`${storage_keys.api_last_team_id}:${user?.id}`, null);
  const setLastTeamId = t6;
  const [show_get_started, setShowGetStarted] = useLocalStorage(`${storage_keys.show_get_started}:${user?.id}`, true);
  const [nav_collapsed, setNavCollapsed] = useLocalStorage("pref:nav_collapsed", false);
  const {
    is_degraded_banner_active,
    setDismissedAt: dismissDegradedBanner
  } = useIsDegradedBanner();
  let t7;
  let t8;
  if ($[6] !== location) {
    t7 = () => {
      setPrevLocation(prev_location_cache || location);
      prev_location_cache = location;
    };
    t8 = [location];
    $[6] = location;
    $[7] = t7;
    $[8] = t8;
  } else {
    t7 = $[7];
    t8 = $[8];
  }
  useEffect(t7, t8);
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = APIMethodKeys.teams.listAll();
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] !== HookdeckAPI.teams) {
    t10 = () => HookdeckAPI.teams.listAll();
    $[10] = HookdeckAPI.teams;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  const {
    data: teams,
    mutate: mutateTeams
  } = useSWR(t9, t10);
  let t11;
  if ($[12] !== HookdeckAPI.team_id || $[13] !== is_eg_type) {
    t11 = is_eg_type && HookdeckAPI.team_id && APIMethodKeys.sourceTypes.list();
    $[12] = HookdeckAPI.team_id;
    $[13] = is_eg_type;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== HookdeckAPI.sourceTypes) {
    t12 = () => HookdeckAPI.sourceTypes.list();
    $[15] = HookdeckAPI.sourceTypes;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  const {
    data: source_types
  } = useSWR(t11, t12);
  let t13;
  if ($[17] !== HookdeckAPI.team_id || $[18] !== is_eg_type) {
    t13 = is_eg_type && HookdeckAPI.team_id && APIMethodKeys.destinationTypes.list();
    $[17] = HookdeckAPI.team_id;
    $[18] = is_eg_type;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== HookdeckAPI.destinationTypes) {
    t14 = () => HookdeckAPI.destinationTypes.list();
    $[20] = HookdeckAPI.destinationTypes;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  const {
    data: destination_types
  } = useSWR(t13, t14);
  let t15;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = APIMethodKeys.organizations.listAll();
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] !== HookdeckAPI.organizations) {
    t16 = () => HookdeckAPI.organizations.listAll();
    $[23] = HookdeckAPI.organizations;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  const {
    data: organizations,
    mutate: mutateOrganizations,
    error: organizations_error
  } = useSWR(t15, t16);
  let t17;
  if ($[25] !== HookdeckAPI.team_id || $[26] !== is_eg_type) {
    t17 = is_eg_type && HookdeckAPI.team_id && [APIMethodKeys.webhooks.list({
      limit: 1
    }), HookdeckAPI.team_id];
    $[25] = HookdeckAPI.team_id;
    $[26] = is_eg_type;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] !== HookdeckAPI.webhooks) {
    t18 = () => HookdeckAPI.webhooks.list({
      limit: 1
    });
    $[28] = HookdeckAPI.webhooks;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  const {
    data: webhooks,
    mutate: mutateHasConnection
  } = useSWR(t17, t18);
  let t19;
  if ($[30] !== organization) {
    t19 = organization && APIMethodKeys.organizations.listMembers(organization.id);
    $[30] = organization;
    $[31] = t19;
  } else {
    t19 = $[31];
  }
  let t20;
  if ($[32] !== HookdeckAPI.organizations) {
    t20 = () => HookdeckAPI.organizations.listMembers();
    $[32] = HookdeckAPI.organizations;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  const {
    data: members
  } = useSWR(t19, t20);
  let t21;
  if ($[34] !== organization) {
    t21 = organization && APIMethodKeys.organizations.listMemberInvites(organization.id);
    $[34] = organization;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  let t22;
  if ($[36] !== HookdeckAPI.organizations) {
    t22 = () => HookdeckAPI.organizations.listMemberInvites();
    $[36] = HookdeckAPI.organizations;
    $[37] = t22;
  } else {
    t22 = $[37];
  }
  const {
    data: invites
  } = useSWR(t21, t22);
  let t23;
  if ($[38] !== is_eg_type) {
    t23 = is_eg_type && APIMethodKeys.sources.list();
    $[38] = is_eg_type;
    $[39] = t23;
  } else {
    t23 = $[39];
  }
  let t24;
  if ($[40] !== HookdeckAPI.sources) {
    t24 = () => HookdeckAPI.sources.list();
    $[40] = HookdeckAPI.sources;
    $[41] = t24;
  } else {
    t24 = $[41];
  }
  const {
    data: sources
  } = useSWR(t23, t24);
  let t25;
  if ($[42] !== is_eg_type) {
    t25 = is_eg_type && APIMethodKeys.destinations.list();
    $[42] = is_eg_type;
    $[43] = t25;
  } else {
    t25 = $[43];
  }
  let t26;
  if ($[44] !== HookdeckAPI.destinations) {
    t26 = () => HookdeckAPI.destinations.list();
    $[44] = HookdeckAPI.destinations;
    $[45] = t26;
  } else {
    t26 = $[45];
  }
  const {
    data: destinations
  } = useSWR(t25, t26);
  const has_sources = !!sources && sources?.count > 0;
  const has_connection = !!webhooks && webhooks?.count > 0;
  const has_created_connections = !!(!!sources?.count || !!destinations?.count);
  const has_team_members_or_invites = !!members && members?.length > 1 || !!invites && invites?.length > 0;
  let total_pending_steps = 2;
  if (has_connection) {
    total_pending_steps = 1;
  }
  if (has_team_members_or_invites) {
    total_pending_steps = total_pending_steps - 1;
  }
  const get_started_pending_steps = total_pending_steps;
  let t27;
  if ($[46] !== is_eg_type || $[47] !== team || $[48] !== user) {
    t27 = is_eg_type && user && team && [APIMethodKeys.cli_clients.list({
      active: true
    }), team.id];
    $[46] = is_eg_type;
    $[47] = team;
    $[48] = user;
    $[49] = t27;
  } else {
    t27 = $[49];
  }
  let t28;
  if ($[50] !== HookdeckAPI.cli_clients) {
    t28 = () => HookdeckAPI.cli_clients.getSessions({
      active: true
    });
    $[50] = HookdeckAPI.cli_clients;
    $[51] = t28;
  } else {
    t28 = $[51];
  }
  let t29;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = {
      refreshInterval: 10000
    };
    $[52] = t29;
  } else {
    t29 = $[52];
  }
  const {
    data: active_cli_sessions
  } = useSWR(t27, t28, t29);
  let t30;
  if ($[53] !== HookdeckAPI.team_id || $[54] !== setLastTeamId) {
    t30 = () => {
      if (HookdeckAPI.team_id) {
        setLastTeamId(HookdeckAPI.team_id);
      }
    };
    $[53] = HookdeckAPI.team_id;
    $[54] = setLastTeamId;
    $[55] = t30;
  } else {
    t30 = $[55];
  }
  const windowRef = useEventListener("focus", t30);
  let t31;
  if ($[56] !== mutateContext || $[57] !== mutateOrganizations || $[58] !== mutateTeams || $[59] !== setAPITeamId || $[60] !== setLastTeamId || $[61] !== setSession) {
    t31 = () => {
      setAPITeamId(null);
      setLastTeamId(null);
      setSession({
        team_id: null
      });
      mutateTeams(undefined);
      mutateContext(undefined);
      mutateOrganizations(undefined);
    };
    $[56] = mutateContext;
    $[57] = mutateOrganizations;
    $[58] = mutateTeams;
    $[59] = setAPITeamId;
    $[60] = setLastTeamId;
    $[61] = setSession;
    $[62] = t31;
  } else {
    t31 = $[62];
  }
  const clearContextStates = t31;
  let t32;
  if ($[63] !== clearContextStates || $[64] !== history) {
    t32 = () => {
      clearContextStates();
      return history.replace("/projects");
    };
    $[63] = clearContextStates;
    $[64] = history;
    $[65] = t32;
  } else {
    t32 = $[65];
  }
  const resetContext = t32;
  let t33;
  if ($[66] !== HookdeckAPI.team_id || $[67] !== last_team_id || $[68] !== search.query.team_id || $[69] !== session || $[70] !== setAPITeamId || $[71] !== teams || $[72] !== user?.last_team_id || $[73] !== windowRef) {
    t33 = () => {
      windowRef.current = window;
      const team_id = search.query.team_id ?? session.team_id ?? last_team_id ?? user?.last_team_id;
      if (!team_id && teams && teams.length > 0) {
        setAPITeamId(teams[0].id);
      }
      if (team_id && !HookdeckAPI.team_id) {
        setAPITeamId(team_id);
      }
      if (search.query.team_id && search.query.team_id !== HookdeckAPI.team_id) {
        setAPITeamId(search.query.team_id);
      }
    };
    $[66] = HookdeckAPI.team_id;
    $[67] = last_team_id;
    $[68] = search.query.team_id;
    $[69] = session;
    $[70] = setAPITeamId;
    $[71] = teams;
    $[72] = user?.last_team_id;
    $[73] = windowRef;
    $[74] = t33;
  } else {
    t33 = $[74];
  }
  let t34;
  if ($[75] !== teams) {
    t34 = [teams];
    $[75] = teams;
    $[76] = t34;
  } else {
    t34 = $[76];
  }
  useEffect(t33, t34);
  let t35;
  if ($[77] !== context_error || $[78] !== last_team_id || $[79] !== location.pathname || $[80] !== resetContext || $[81] !== session || $[82] !== toasts || $[83] !== user) {
    t35 = () => {
      if (user && context_error && context_error?.status === 401 && !location.pathname.includes("/projects") && !location.pathname.includes("/onboarding")) {
        if (session.team_id || last_team_id) {
          toasts.addToast("warning", "Unauthorized to access the latest project.");
        }
        resetContext();
      }
    };
    $[77] = context_error;
    $[78] = last_team_id;
    $[79] = location.pathname;
    $[80] = resetContext;
    $[81] = session;
    $[82] = toasts;
    $[83] = user;
    $[84] = t35;
  } else {
    t35 = $[84];
  }
  const t36 = user && context_error && context_error?.status === 401;
  let t37;
  if ($[85] !== t36) {
    t37 = [t36];
    $[85] = t36;
    $[86] = t37;
  } else {
    t37 = $[86];
  }
  useEffect(t35, t37);
  let t38;
  if ($[87] !== context_error || $[88] !== location.pathname || $[89] !== location.search || $[90] !== organizations_error) {
    t38 = () => {
      const mfa_error = [organizations_error, context_error].find(_temp);
      if (mfa_error && !location.pathname.includes("/setup-mfa") && !location.pathname.includes("/signin")) {
        const error_response = mfa_error.response as {
          blocking_organization?: {
            id: string;
            name: string;
          };
          redirect?: string;
        };
        const blocking_org = error_response?.blocking_organization;
        const setup_url = new URL("/setup-mfa", window.location.origin);
        if (blocking_org?.id) {
          setup_url.searchParams.set("organization_id", blocking_org.id);
        }
        setup_url.searchParams.set("redirect", location.pathname + location.search);
        window.location.replace(setup_url.toString());
      }
    };
    $[87] = context_error;
    $[88] = location.pathname;
    $[89] = location.search;
    $[90] = organizations_error;
    $[91] = t38;
  } else {
    t38 = $[91];
  }
  const t39 = organizations_error?.status;
  const t40 = organizations_error?.response;
  const t41 = context_error?.status;
  const t42 = context_error?.response;
  let t43;
  if ($[92] !== location.pathname || $[93] !== location.search || $[94] !== t39 || $[95] !== t40 || $[96] !== t41 || $[97] !== t42) {
    t43 = [t39, t40, t41, t42, location.pathname, location.search];
    $[92] = location.pathname;
    $[93] = location.search;
    $[94] = t39;
    $[95] = t40;
    $[96] = t41;
    $[97] = t42;
    $[98] = t43;
  } else {
    t43 = $[98];
  }
  useEffect(t38, t43);
  let t44;
  if ($[99] !== HookdeckAPI.team_id || $[100] !== context_error || $[101] !== history || $[102] !== location.pathname || $[103] !== location.search || $[104] !== organization || $[105] !== organizations || $[106] !== session_context || $[107] !== team || $[108] !== teams || $[109] !== user) {
    t44 = () => {
      if (!teams || !organizations || !user || HookdeckAPI.team_id && !session_context && !context_error) {
        return;
      }
      if (user.is_guest) {
        if (window.location.pathname.indexOf("/cli/events") === 0) {
          return window.location.replace(process.env.CONSOLE_URL);
        }
        return window.location.replace("/signup");
      }
      if (teams.length === 0 && !organization && !team && !location.pathname.includes("/onboarding") && !location.pathname.includes("/cli-auth")) {
        if (organizations.length > 0) {
          return history.replace("/projects");
        }
        const redirect_param = location.pathname !== "/" ? encodeURIComponent(`${location.pathname}${location.search}`) : "";
        return history.replace(redirect_param ? `/onboarding?redirect=${redirect_param}` : "/onboarding");
      }
    };
    $[99] = HookdeckAPI.team_id;
    $[100] = context_error;
    $[101] = history;
    $[102] = location.pathname;
    $[103] = location.search;
    $[104] = organization;
    $[105] = organizations;
    $[106] = session_context;
    $[107] = team;
    $[108] = teams;
    $[109] = user;
    $[110] = t44;
  } else {
    t44 = $[110];
  }
  const t45 = context_error?.response;
  let t46;
  if ($[111] !== organizations || $[112] !== session_context || $[113] !== t45 || $[114] !== teams || $[115] !== user) {
    t46 = [user, teams, organizations, session_context, t45];
    $[111] = organizations;
    $[112] = session_context;
    $[113] = t45;
    $[114] = teams;
    $[115] = user;
    $[116] = t46;
  } else {
    t46 = $[116];
  }
  useEffect(t44, t46);
  let t47;
  if ($[117] !== organization || $[118] !== subscription?.outpost_plan || $[119] !== subscription?.plan || $[120] !== subscription?.retention_days || $[121] !== team) {
    t47 = () => {
      if (organization) {
        posthog?.group("organization", organization.id);
      }
      if (team) {
        posthog?.group("team", team.id);
      }
      if ((window as any).$crisp) {
        (window as any).$crisp.push(["set", "session:data", [[["organization_id", organization?.id || ""], ["organization_name", organization?.name || ""], ["organization_crm_url", organization ? `https://crm.hookdeck.com/companies/${organization.id}` : ""], ["feature_flags", JSON.stringify(organization?.feature_flags || {})], ["team_id", team?.id || ""], ["team_name", team?.name || ""], ["max_events_per_second", team?.max_events_per_second || ""], ["retention_days", subscription?.retention_days || ""], ["plan", subscription?.plan || ""], ["outpost_plan", subscription?.outpost_plan || ""]]]]);
      }
    };
    $[117] = organization;
    $[118] = subscription?.outpost_plan;
    $[119] = subscription?.plan;
    $[120] = subscription?.retention_days;
    $[121] = team;
    $[122] = t47;
  } else {
    t47 = $[122];
  }
  let t48;
  if ($[123] !== organization || $[124] !== subscription || $[125] !== team) {
    t48 = [team, subscription, organization];
    $[123] = organization;
    $[124] = subscription;
    $[125] = team;
    $[126] = t48;
  } else {
    t48 = $[126];
  }
  useEffect(t47, t48);
  let t49;
  if ($[127] !== clearContextStates) {
    t49 = () => {
      clearContextStates();
      window.localStorage.clear();
      window.sessionStorage.clear();
      window.location.replace("/signin");
    };
    $[127] = clearContextStates;
    $[128] = t49;
  } else {
    t49 = $[128];
  }
  const logout = t49;
  let t50;
  if ($[129] !== active_cli_sessions) {
    t50 = active_cli_sessions || [];
    $[129] = active_cli_sessions;
    $[130] = t50;
  } else {
    t50 = $[130];
  }
  const t51 = team || (teams ? teams.length > 0 ? teams[0] : null : undefined);
  let t52;
  if ($[131] !== mutateTeams) {
    t52 = () => mutateTeams();
    $[131] = mutateTeams;
    $[132] = t52;
  } else {
    t52 = $[132];
  }
  let t53;
  if ($[133] !== mutateOrganizations) {
    t53 = () => mutateOrganizations();
    $[133] = mutateOrganizations;
    $[134] = t53;
  } else {
    t53 = $[134];
  }
  let t54;
  if ($[135] !== mutateContext || $[136] !== mutateTeams) {
    t54 = team_0 => {
      mutateContext(session_context_0 => ({
        ...session_context_0,
        team: team_0
      }) as any);
      mutateTeams(current_teams => {
        if (current_teams === undefined) {
          return current_teams;
        }
        return current_teams.map(current_team => current_team.id === team_0.id ? {
          ...current_team,
          ...team_0
        } : current_team);
      }, false);
    };
    $[135] = mutateContext;
    $[136] = mutateTeams;
    $[137] = t54;
  } else {
    t54 = $[137];
  }
  let t55;
  let t56;
  if ($[138] !== mutateContext) {
    t55 = subscription_0 => mutateContext(session_context_1 => ({
      ...session_context_1,
      subscription: subscription_0
    }) as any);
    t56 = organization_0 => mutateContext(session_context_2 => ({
      ...session_context_2,
      organization: organization_0
    }) as any);
    $[138] = mutateContext;
    $[139] = t55;
    $[140] = t56;
  } else {
    t55 = $[139];
    t56 = $[140];
  }
  let t57;
  if ($[141] !== destination_types || $[142] !== dismissDegradedBanner || $[143] !== get_started_pending_steps || $[144] !== has_connection || $[145] !== has_created_connections || $[146] !== has_sources || $[147] !== has_team_members_or_invites || $[148] !== is_degraded_banner_active || $[149] !== logout || $[150] !== mutateHasConnection || $[151] !== nav_collapsed || $[152] !== organization || $[153] !== organization_role || $[154] !== organizations || $[155] !== prev_location || $[156] !== resetContext || $[157] !== session || $[158] !== setNavCollapsed || $[159] !== setSession || $[160] !== setShowGetStarted || $[161] !== show_get_started || $[162] !== source_types || $[163] !== subscription || $[164] !== t50 || $[165] !== t51 || $[166] !== t52 || $[167] !== t53 || $[168] !== t54 || $[169] !== t55 || $[170] !== t56 || $[171] !== team_role || $[172] !== teams || $[173] !== user) {
    t57 = {
      active_cli_sessions: t50,
      user,
      team: t51,
      source_types,
      destination_types,
      organization,
      organizations,
      teams,
      team_role,
      organization_role,
      subscription,
      mutateTeams: t52,
      mutateOrganizations: t53,
      mutateTeam: t54,
      mutateSubscription: t55,
      mutateOrganization: t56,
      resetContext,
      session,
      setSession,
      has_sources,
      has_connection,
      has_created_connections,
      mutateHasConnection,
      has_team_members_or_invites,
      get_started_pending_steps,
      show_get_started,
      setShowGetStarted,
      prev_location,
      nav_collapsed,
      setNavCollapsed,
      is_degraded_banner_active,
      dismissDegradedBanner,
      logout
    };
    $[141] = destination_types;
    $[142] = dismissDegradedBanner;
    $[143] = get_started_pending_steps;
    $[144] = has_connection;
    $[145] = has_created_connections;
    $[146] = has_sources;
    $[147] = has_team_members_or_invites;
    $[148] = is_degraded_banner_active;
    $[149] = logout;
    $[150] = mutateHasConnection;
    $[151] = nav_collapsed;
    $[152] = organization;
    $[153] = organization_role;
    $[154] = organizations;
    $[155] = prev_location;
    $[156] = resetContext;
    $[157] = session;
    $[158] = setNavCollapsed;
    $[159] = setSession;
    $[160] = setShowGetStarted;
    $[161] = show_get_started;
    $[162] = source_types;
    $[163] = subscription;
    $[164] = t50;
    $[165] = t51;
    $[166] = t52;
    $[167] = t53;
    $[168] = t54;
    $[169] = t55;
    $[170] = t56;
    $[171] = team_role;
    $[172] = teams;
    $[173] = user;
    $[174] = t57;
  } else {
    t57 = $[174];
  }
  const context_0 = t57;
  if (loading_user || !context_error && teams === undefined && organization === undefined && team === undefined && subscription === undefined && (webhooks === undefined || session_context === undefined)) {
    let t58;
    if ($[175] === Symbol.for("react.memo_cache_sentinel")) {
      t58 = <FullscreenLayout center={true} no_decorations={true}><Icon icon="loading" muted={true} /></FullscreenLayout>;
      $[175] = t58;
    } else {
      t58 = $[175];
    }
    return t58;
  }
  const t58 = team?.id;
  let t59;
  if ($[176] !== HookdeckAPI.team_id) {
    t59 = getAddTeamKeyMiddleWare(HookdeckAPI.team_id);
    $[176] = HookdeckAPI.team_id;
    $[177] = t59;
  } else {
    t59 = $[177];
  }
  let t60;
  if ($[178] !== t59) {
    t60 = {
      use: [t59]
    };
    $[178] = t59;
    $[179] = t60;
  } else {
    t60 = $[179];
  }
  let t61;
  if ($[180] !== children || $[181] !== t58 || $[182] !== t60) {
    t61 = <SWRConfig key={t58} value={t60}>{children}</SWRConfig>;
    $[180] = children;
    $[181] = t58;
    $[182] = t60;
    $[183] = t61;
  } else {
    t61 = $[183];
  }
  let t62;
  if ($[184] !== context_0 || $[185] !== t61) {
    t62 = <DashboardContext.Provider value={context_0}>{t61}</DashboardContext.Provider>;
    $[184] = context_0;
    $[185] = t61;
    $[186] = t62;
  } else {
    t62 = $[186];
  }
  return t62;
};
export const WithUserOnly: React.FC<PropsWithChildren> = t0 => {
  const $ = _c(7);
  const {
    children
  } = t0;
  const {
    user,
    user_error
  } = useContext(UserContext);
  let t1;
  if ($[0] !== user_error?.status) {
    t1 = () => {
      if (user_error?.status === 401) {
        const redirect_param = encodeURIComponent(`${location.pathname}${location.search}`);
        return window.location.replace(`/signin?redirect=${redirect_param}`);
      }
    };
    $[0] = user_error?.status;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== user_error) {
    t2 = [user_error];
    $[2] = user_error;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== children || $[5] !== user) {
    t3 = user ? <>{children}</> : <FullscreenLayout center={true} no_decorations={true}><Icon icon="loading" muted={true} /></FullscreenLayout>;
    $[4] = children;
    $[5] = user;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
export const WithTeamOnly = t0 => {
  const $ = _c(1);
  const {
    children
  } = t0;
  const {
    team,
    subscription
  } = useContext(DashboardContext);
  if (!team || !subscription) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <FullscreenLayout center={true} no_decorations={true}><Icon icon="loading" muted={true} /></FullscreenLayout>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  return children;
};
function _temp(err) {
  return err?.status === 403 && typeof err?.response === "object" && err?.response !== null && (err?.response as {
    error?: string;
  })?.error === "mfa_setup_required";
}